/**
 * useReadingTracker
 *
 * A React hook that manages a WebSocket-based reading session against the
 * Laravel Reverb backend.
 *
 * Features:
 *  - Establishes a private Echo channel (`reading.{zlUid}`) on `startSession`
 *  - Sends an initial `client-read-tick` whisper as soon as the connection
 *    is confirmed
 *  - Automatically whispers a tick every `tickIntervalMs` ms (default 30 s)
 *    while the connection is live
 *  - Handles `RewardUnlocked`, `SessionAutoClosed`, and `TickRejected` server
 *    broadcast events
 *  - Gracefully ends the session (whisper + disconnect) on `stopSession` or
 *    component unmount
 *
 * Usage:
 *   const { startSession, stopSession, isConnected } = useReadingTracker({
 *     zlUid: 'user_abc123',
 *     sourceId: 'course-101',
 *     sourceName: 'My Course',
 *     source: 'courses',
 *     sourceType: 'course',
 *     token: authToken,
 *   });
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { initEcho } from "../config/echo";

const DEFAULT_TICK_INTERVAL_MS = 30_000; // 30 seconds
const DEFAULT_ACTIVE_SECONDS = 30; // satisfies the 5–65 backend constraint

/**
 * Custom hook to manage a WebSocket reading-tracker session.
 *
 * @param {object} options
 * @param {string}   options.zlUid              Zebralearn user ID
 * @param {string}   options.sourceId           Course / content ID
 * @param {string}   options.sourceName         Course / content title
 * @param {string}   options.source             'books' | 'courses' | 'schools' | 'short_books' | 'live_streams'
 * @param {string}   options.sourceType         'course' | 'digital' | etc.
 * @param {string}   [options.token]            Bearer auth token (omit to use cookie auth)
 * @param {string}   [options.subSource]        Sub-source type e.g. 'video'
 * @param {string}   [options.subSourceId]      Chapter / video ID
 * @param {string}   [options.subSourceName]    Chapter / video name
 * @param {string}   [options.currentPage]      Current page / position as string
 * @param {function} [options.getPagesRead]      Callback returning new unique pages read
 * @param {number}   [options.tickIntervalMs]   Tick interval in ms (default: 30000)
 * @param {function} [options.onRewardUnlocked]  Called on RewardUnlocked server event
 * @param {function} [options.onSessionAutoClosed] Called on SessionAutoClosed server event
 * @param {function} [options.onTickRejected]   Called on TickRejected server event
 */
export const useReadingTracker = ({
  zlUid,
  sourceId,
  sourceName,
  token,
  subSource,
  source,
  sourceType,
  subSourceId,
  subSourceName,
  tickIntervalMs = DEFAULT_TICK_INTERVAL_MS,
  onRewardUnlocked,
  onSessionAutoClosed,
  onTickRejected,
  currentPage,
  getPagesRead,
}) => {
  const [isConnected, setIsConnected] = useState(false);

  // Stable refs — never trigger re-renders
  const activeSessionIdRef = useRef(null);
  const channelRef = useRef(null);
  const echoRef = useRef(null);
  const isConnectedRef = useRef(false);
  const currentPageRef = useRef(currentPage);
  const getPagesReadRef = useRef(getPagesRead);

  // Keep refs in sync with latest prop values
  useEffect(() => {
    getPagesReadRef.current = getPagesRead;
  });

  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // ─── sendTick ─────────────────────────────────────────────────────────────

  const sendTick = useCallback(
    (sessionId, activeSeconds, isFirst = false) => {
      if (!channelRef.current || !isConnectedRef.current) return;

      const pagesRead = getPagesReadRef.current?.() ?? 0;

      const payload = {
        session_id: sessionId,
        source_id: sourceId,
        source: source,
        source_type: sourceType,
        source_name: sourceName,
        sub_source: subSource,
        sub_source_id: subSourceId,
        sub_source_name: subSourceName,
        active_seconds: activeSeconds,
        pages_read: pagesRead,
        current_page: currentPageRef.current,
        platform: "website",
        zl_uid: zlUid,
        client_timestamp: new Date().toISOString(),
      };

      // Echo automatically prefixes whisper events with 'client-'
      // so this becomes 'client-read-tick' on the wire.
      channelRef.current.whisper("read-tick", payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sourceId,
      sourceName,
      source,
      sourceType,
      subSourceId,
      subSourceName,
      zlUid,
    ],
  );

  // ─── stopSession ──────────────────────────────────────────────────────────

  const stopSession = useCallback(
    (reason = "user_closed") => {
      const sessionId = activeSessionIdRef.current;
      if (!sessionId) return;

      if (
        channelRef.current &&
        isConnectedRef.current &&
        reason !== "auto_closed"
      ) {
        channelRef.current.whisper("client-session-end", {
          session_id: sessionId,
          zl_uid: zlUid,
          reason,
        });
      }

      if (echoRef.current) {
        echoRef.current.disconnect();
      }

      activeSessionIdRef.current = null;
      channelRef.current = null;
      echoRef.current = null;
      isConnectedRef.current = false;
      setIsConnected(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zlUid],
  );

  // ─── startSession ─────────────────────────────────────────────────────────

  const startSession = useCallback(() => {
    // Prevent duplicate sessions
    if (activeSessionIdRef.current) return;

    activeSessionIdRef.current = uuidv4();
    const echo = initEcho(token);
    if (!echo) {
      console.warn("[ReadingTracker] Echo init failed");
      return;
    }
    echoRef.current = echo;

    // Subscribe to the user's private channel
    const channel = echo.private(`reading.${zlUid}`);
    channelRef.current = channel;

    // ── Connection established ──────────────────────────────────────────────
    echo.connector.pusher.connection.bind("connected", () => {
      isConnectedRef.current = true;
      setIsConnected(true);

      if (activeSessionIdRef.current) {
        sendTick(activeSessionIdRef.current, DEFAULT_ACTIVE_SECONDS, true);
      }
    });

    // ── Server broadcast: RewardUnlocked ────────────────────────────────────
    channel.listen(".RewardUnlocked", (data) => {
      onRewardUnlocked?.(data);
    });

    // ── Server broadcast: SessionAutoClosed ─────────────────────────────────
    channel.listen(".SessionAutoClosed", (data) => {
      console.warn("[ReadingTracker] ⚠️ Session auto-closed by server", data);
      onSessionAutoClosed?.(data);
      stopSession("auto_closed");
    });

    // ── Server broadcast: TickRejected ──────────────────────────────────────
    channel.listen(".TickRejected", (data) => {
      console.warn("[ReadingTracker] ❌ Tick rejected:", data.reason, data);
      onTickRejected?.(data);
    });
  }, [
    token,
    zlUid,
    sendTick,
    stopSession,
    onRewardUnlocked,
    onSessionAutoClosed,
    onTickRejected,
  ]);

  // ─── Auto-tick effect ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!isConnected || !activeSessionIdRef.current) return;

    const interval = setInterval(() => {
      if (activeSessionIdRef.current) {
        sendTick(activeSessionIdRef.current, DEFAULT_ACTIVE_SECONDS, false);
      }
    }, tickIntervalMs);

    return () => clearInterval(interval);
  }, [isConnected, sendTick, tickIntervalMs]);

  // ─── Cleanup on unmount ───────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      stopSession("user_closed");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Public API ───────────────────────────────────────────────────────────

  return {
    startSession,
    stopSession,
    isConnected,
    activeSessionId: activeSessionIdRef.current,
  };
};
